if (!customElements.get('slideshow-component')) {
    customElements.define(
        'slideshow-component',
        class SlideshowComponent extends HTMLElement {
            constructor() {
                super();

                this.sliderControls = this.querySelector('.swiper-controls');
                this.sliderInstance = false;
                this.enableSlider = this.dataset.enableSlider === 'true';
                this.selectedIndex = this.selectedIndex;
                this.sectionId = this.dataset.sectionId;
                this.section = this.closest(`.section-${this.sectionId}`);
                this.containerWidth = this.dataset.container;
                this.cleanupScrollAnimations = [];
                this.enableZoom = this.dataset.enableZoom ? this.dataset.enableZoom === 'true' : true;
                this.borderRadius = this.dataset.borderRadius;
                this.zoom = 0;

                // NTR: runs before the enableSlider check, Liquid only knows the block count, not the schedule.
                this.filterScheduledSlides();

                if (!this.enableSlider) return;

                this.setupZoom();
                window.addEventListener('resize', this.setupZoom.bind(this));

                this.initSlider();
            }

            // NTR: dates are read in the visitor's local time. The theme editor shows every slide.
            filterScheduledSlides() {
                if (window.Shopify && window.Shopify.designMode) return;

                const slides = Array.from(this.querySelectorAll('.swiper-slide'));
                if (!slides.some((slide) => slide.dataset.nwfStart || slide.dataset.nwfEnd)) return;

                const now = Date.now();
                const visible = slides.filter((slide) => {
                    const start = this.parseScheduleDate(slide.dataset.nwfStart, false);
                    const end = this.parseScheduleDate(slide.dataset.nwfEnd, true);
                    const show = (start === null || now >= start) && (end === null || now <= end);
                    if (!show) slide.remove();
                    return show;
                });

                if (visible.length === slides.length) return;

                if (visible.length === 0) {
                    this.enableSlider = false;
                    const wrapper = this.closest('.shopify-section') || this.section;
                    if (wrapper) wrapper.style.display = 'none';
                    return;
                }

                if (this.sliderControls) this.updateControlsScheme(visible[0]);

                visible.forEach((slide, index) => {
                    const label = slide.getAttribute('aria-label');
                    if (label)
                        slide.setAttribute(
                            'aria-label',
                            label.replace(/^\d+(.*?)\d+$/, `${index + 1}$1${visible.length}`),
                        );
                });

                if (visible.length === 1) {
                    this.enableSlider = false;
                    this.removeAttribute('data-media-loading');
                    this.sliderControls?.remove();
                    this.sliderControls = null;
                }
            }

            // Accepts "2026-10-31 08:00", "2026-10-31T08:00" and "31.10.2026 08:00". Without a time the
            // start falls on 00:00 and the end on 23:59:59 of that day. Invalid values are ignored.
            parseScheduleDate(value, isEnd) {
                if (!value) return null;

                const str = value.trim();
                let match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/);
                let year, month, day;

                if (match) {
                    [, year, month, day] = match;
                } else {
                    match = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:,?\s+(\d{1,2}):(\d{2}))?$/);
                    if (!match) return null;
                    [, day, month, year] = match;
                }

                const hasTime = match[4] !== undefined;
                const hours = hasTime ? Number(match[4]) : isEnd ? 23 : 0;
                const minutes = hasTime ? Number(match[5]) : isEnd ? 59 : 0;
                if (hours > 23 || minutes > 59) return null;
                const date = new Date(
                    Number(year),
                    Number(month) - 1,
                    Number(day),
                    hours,
                    minutes,
                    isEnd ? 59 : 0,
                    isEnd ? 999 : 0,
                );

                if (date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) return null;

                return date.getTime();
            }

            static get observedAttributes() {
                return ['selected-index'];
            }

            get selectedIndex() {
                return parseInt(this.getAttribute('selected-index')) || 0;
            }

            set selectedIndex(index) {
                this.setAttribute('selected-index', `${index}`);
            }

            setupZoom() {
                if (!this.enableZoom) return;

                this.destroyZoom();

                const pageWidthEl = this.querySelector('.page-width');
                const paddingLeft = window.getComputedStyle(pageWidthEl).getPropertyValue('padding-left');
                this.zoom = Math.min(parseFloat(paddingLeft.replace('px', '')), window.innerWidth * 0.04);

                if (this.zoom > 15 && FoxTheme.a11y.shouldAnimate()) {
                    this.initZoom();
                }
            }

            initSlider() {
                const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

                this.sliderOptions = {
                    slidesPerView: 1,
                    spaceBetween: 10,
                    loop: true,
                    // NWF-217: with slide links the grab cursor hides which slides are clickable.
                    grabCursor: !this.querySelector('.nwf-slide-link'),
                    allowTouchMove: true,
                    threshold: 2,
                    effect: 'fade',
                    on: {
                        init: this.handleAfterInit.bind(this),
                    },
                    fadeEffect: {
                        crossFade: true,
                    },
                };

                if (this.sliderControls) {
                    this.sliderOptions.navigation = {
                        nextEl: this.sliderControls.querySelector('.swiper-button-next'),
                        prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
                    };
                    this.sliderOptions.pagination = {
                        el: this.sliderControls.querySelector('.swiper-pagination'),
                        clickable: true,
                    };
                }

                // Do NOT add pauseOnMouseEnter. Swiper 8 pauses on enter but
                // never re-arms the timer on leave, so the slideshow stops for
                // good. Use the toggle instead (WCAG 2.1 2.2.2).
                const autoplayDelay = parseInt(this.dataset.autoplay);
                this.autoplayEnabled = autoplayDelay > 0 && !FoxTheme.config.motionReduced;

                if (this.autoplayEnabled) {
                    this.sliderOptions = {
                        ...this.sliderOptions,
                        autoplay: {
                            delay: autoplayDelay,
                            disableOnInteraction: false,
                        },
                    };
                }

                this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
                this.sliderInstance.init();

                this.setupAutoplayControls();

                this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

                if (this.sliderInstance) {
                    this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
                    this.onReady(this.selectedElement, this.sliderInstance.slider.slides);

                    // Fix accessibility
                    const focusableElements = FoxTheme.a11y.getFocusableElements(this);
                    focusableElements.forEach((element) => {
                        if (!element.classList.contains('swiper-button') && !element.classList.contains('btn')) {
                            // NWF-217: a click focuses the slide link, so the active slide must not move.
                            // In loop mode the DOM index includes the cloned slides, slideToLoop needs the real one.
                            element.addEventListener('focusin', () => {
                                const slide = element.closest('.swiper-slide');
                                if (!slide || slide.classList.contains('swiper-slide-active')) return;
                                this.sliderInstance.slider.slideToLoop(parseInt(slide.dataset.swiperSlideIndex, 10));
                            });
                        }
                    });
                }
            }

            setupAutoplayControls() {
                this.autoplayToggle =
                    this.sliderControls && this.sliderControls.querySelector('[data-autoplay-toggle]');
                if (!this.autoplayToggle) return;

                const slider = this.sliderInstance.slider;

                if (!this.autoplayEnabled || !slider.autoplay) {
                    this.autoplayToggle.remove();
                    this.autoplayToggle = null;
                    return;
                }

                this.autoplayPausedByUser = false;

                this.autoplayToggle.addEventListener('click', () => {
                    this.autoplayPausedByUser = !this.autoplayPausedByUser;

                    if (this.autoplayPausedByUser) {
                        slider.autoplay.stop();
                    } else {
                        slider.autoplay.start();
                    }

                    this.updateAutoplayToggle();
                });
            }

            updateAutoplayToggle() {
                if (!this.autoplayToggle) return;

                const strings = FoxTheme.accessibilityStrings || {};

                this.autoplayToggle.dataset.paused = String(this.autoplayPausedByUser);

                // Emptied rather than frozen mid-fill: .start() re-arms the FULL
                // delay, so a half-full bar would promise the wrong wait.
                this.toggleAttribute('data-autoplay-paused', this.autoplayPausedByUser);
                this.autoplayToggle.setAttribute(
                    'aria-label',
                    this.autoplayPausedByUser ? strings.playSlideshow : strings.pauseSlideshow,
                );
            }

            onReady(selectedElement) {
                if (selectedElement.dataset.type === 'video') {
                    const videoElement = FoxTheme.utils.displayedMedia(
                        selectedElement.querySelectorAll('video-element'),
                    );
                    videoElement?.play();
                }

                if (FoxTheme.a11y.shouldAnimate()) {
                    const motionEls = selectedElement.querySelectorAll('motion-element');
                    motionEls.forEach((motionEl) => {
                        setTimeout(() => {
                            motionEl && motionEl.refreshAnimation();
                        });
                    });
                }
            }

            handleAfterInit() {
                this.removeAttribute('data-media-loading');

                // Fix active bullet not transition on the first time.
                if (this.sliderControls) {
                    const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

                    if (activeBullet) {
                        activeBullet.classList.remove('swiper-pagination-bullet-active');
                        activeBullet.offsetHeight; // Trigger reflow.
                        activeBullet.classList.add('swiper-pagination-bullet-active');
                    }
                }
            }

            handleSlideChange(swiper) {
                const { slides, realIndex, activeIndex } = swiper;
                this.selectedIndex = realIndex;

                this.updateControlsScheme(slides[activeIndex]);
            }

            attributeChangedCallback(name, oldValue, newValue) {
                if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
                    const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
                    const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

                    fromElements.forEach((fromElement) => {
                        if (fromElement.dataset.type === 'video') {
                            const videoElement = FoxTheme.utils.displayedMedia([
                                fromElement.querySelector('video-element'),
                            ]);
                            videoElement && videoElement.pause();
                        }

                        const motionEls = fromElement.querySelectorAll('motion-element');
                        if (FoxTheme.a11y.shouldAnimate()) {
                            motionEls.forEach((motionEl) => {
                                if (motionEl.hasAttribute('data-text')) {
                                    motionEl.resetAnimation();
                                }
                            });
                        }
                    });

                    toElements.forEach((toElement) => {
                        setTimeout(() => {
                            if (toElement.classList.contains('swiper-slide-active')) {
                                if (toElement.dataset.type === 'video') {
                                    const videoElement = FoxTheme.utils.displayedMedia([
                                        toElement.querySelector('video-element'),
                                    ]);
                                    videoElement && videoElement.play();
                                }

                                const motionEls = toElement.querySelectorAll('motion-element');
                                if (FoxTheme.a11y.shouldAnimate()) {
                                    motionEls.forEach((motionEl) => {
                                        setTimeout(() => {
                                            motionEl && motionEl.refreshAnimation();
                                        });
                                    });
                                }
                            }
                        });
                    });
                }
            }

            updateControlsScheme(activeSlide) {
                if (this.sliderControls) {
                    const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
                        className.startsWith('color-'),
                    );
                    classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
                    const colorScheme = activeSlide.dataset.colorScheme;
                    this.sliderControls.classList.add(colorScheme);
                }
            }

            initZoom() {
                if (this.containerWidth !== 'full') return;

                const motionTarget = {
                    target: this.section,
                    offset: [
                        [-0.1, 0],
                        [0.7, 0],
                    ],
                };

                this.querySelectorAll('.slideshow__item-wrap').forEach((slide) => {
                    const slideBg = slide.querySelector('.slideshow__bg');

                    this.cleanupScrollAnimations.push(
                        FoxTheme.Motion.scroll(
                            FoxTheme.Motion.animate(slideBg, {
                                transform: [`scale(1) translateZ(0)`, `scale(1.1) translateZ(0)`],
                            }),
                            motionTarget,
                        ),
                    );
                });

                this.cleanupScrollAnimations.push(
                    FoxTheme.Motion.scroll(
                        FoxTheme.Motion.animate(this, {
                            clipPath: [
                                `inset(0%)`,
                                `inset(${Math.min(50, this.zoom)}px ${this.zoom}px round ${this.borderRadius}px)`,
                            ],
                        }),
                        motionTarget,
                    ),
                );

                if (this.sliderControls) {
                    this.cleanupScrollAnimations.push(
                        FoxTheme.Motion.scroll(
                            FoxTheme.Motion.animate(this.sliderControls, {
                                bottom: [`0`, `${Math.min(50, this.zoom)}px`],
                            }),
                            motionTarget,
                        ),
                    );
                }
            }

            destroyZoom() {
                this.cleanupScrollAnimations.forEach((fn) => fn());
                this.cleanupScrollAnimations = [];
                this.style.clipPath = 'none';
                if (this.sliderControls) this.sliderControls.style.bottom = 0;
            }
        },
    );
}
